/**
 * 「列ノード → 固定行セルノード」の同一性の対応付け。
 *
 * 固定行セルは列の複製なので、正規化のたびに「列のこのノードは固定行のどのノードか」を
 * 現在の状態(列と固定行が同じ形)を突き合わせて id / uuid の対応を作り、複製時に引き継ぐ。
 */

import { TemplateNodeSchema } from '@ulysses-inc/harami_api_client'
import type { TemplateNodesDict } from 'src/exShared/types/types'
import { getLogicBranchQuestions, getQuestionLogicNodes } from '../selector'
import type { CellNodeIdentity, ResolveCellIdentity } from './cloneSubtree'

/**
 * 「列ノード id → 固定行ノードの同一性(id / uuid)」を引くリゾルバを作る。
 *
 * 列と固定行は常に同じ形(編集中は構造の同期が維持する)である前提で、
 * 質問と条件分岐を交互に、両方のツリーを同じ順序でたどって対応させる。
 * 形が一致しないなら構造が壊れているので throw する。
 *
 * ツリーを平らに並べてから順番に比べる方法は使えない。次の2つはどちらも
 * [質問, 条件分岐, 質問, 条件分岐, 質問] と並び、区別できないため。
 *
 *   A: 質問                     B: 質問
 *      └─ 条件分岐1                ├─ 条件分岐1
 *          └─ 分岐質問a            │   └─ 分岐質問a
 *              └─ 条件分岐2        └─ 条件分岐2
 *                  └─ 分岐質問b        └─ 分岐質問b
 *
 * 取り違えると、列のノードに別の位置の固定行ノードを対応させ、保存時に id / uuid が入れ替わる。
 */
export const buildIdentityResolver = (
  columnNode: TemplateNodeSchema,
  cellNode: TemplateNodeSchema,
  templateNodes: TemplateNodesDict,
): ResolveCellIdentity => {
  const shapeMismatchError = () =>
    new Error(
      `固定行セル(id: ${cellNode.id})の構造が列(id: ${columnNode.id})と一致しません`,
    )

  const identityByColumnNodeId = new Map<number, CellNodeIdentity>()

  const pair = (
    columnSideNode: TemplateNodeSchema,
    cellSideNode: TemplateNodeSchema,
  ) => {
    // nodes は種別で絞る前の子の id。条件分岐や分岐質問を取り出すセレクタは種別で絞るので、
    // 絞ったあとの数しか見ないと、固定行側にだけ別種別のノードがぶら下がっていても気づけない。
    if (
      cellSideNode.type !== columnSideNode.type ||
      cellSideNode.nodes.length !== columnSideNode.nodes.length
    ) {
      throw shapeMismatchError()
    }
    identityByColumnNodeId.set(columnSideNode.id, {
      id: cellSideNode.id,
      uuid: cellSideNode.uuid,
    })
  }

  /** 質問どうしを対応させ、配下の条件分岐へ降りる。 */
  const walkQuestion = (
    columnSideNode: TemplateNodeSchema,
    cellSideNode: TemplateNodeSchema,
  ) => {
    pair(columnSideNode, cellSideNode)
    walkChildren(
      getQuestionLogicNodes(columnSideNode, templateNodes),
      getQuestionLogicNodes(cellSideNode, templateNodes),
      walkLogic,
    )
  }

  /** 条件分岐どうしを対応させ、配下の分岐質問へ降りる。 */
  const walkLogic = (
    columnSideNode: TemplateNodeSchema,
    cellSideNode: TemplateNodeSchema,
  ) => {
    pair(columnSideNode, cellSideNode)
    walkChildren(
      getLogicBranchQuestions(columnSideNode, templateNodes),
      getLogicBranchQuestions(cellSideNode, templateNodes),
      walkQuestion,
    )
  }

  const walkChildren = (
    columnSideChildren: TemplateNodeSchema[],
    cellSideChildren: TemplateNodeSchema[],
    walk: (a: TemplateNodeSchema, b: TemplateNodeSchema) => void,
  ) => {
    // 種別で絞ったあとの数。絞る前の数は pair が見る。
    if (columnSideChildren.length !== cellSideChildren.length) {
      throw shapeMismatchError()
    }
    for (const [index, columnSideChild] of columnSideChildren.entries()) {
      walk(columnSideChild, cellSideChildren[index]!)
    }
  }

  walkQuestion(columnNode, cellNode)
  return columnNodeId => identityByColumnNodeId.get(columnNodeId)
}
